import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { environment } from '@environment';
import { patchState, signalState } from '@ngrx/signals';
import { firstValueFrom, map } from 'rxjs';
import { Company } from '~/domain/types/company';
import { Pageable, Paginated, Pagination, Searchable, Sort, Sortable } from '~/domain/types/listings';
import { CreateUser, UpdateUser, User } from '~/domain/types/user';
import { pluck } from '~/utils/common';

type State = {
  q: string;
  sort: Sort;
  pagination: Pagination;
  users: User[] | null;
};

@Service()
export class UsersService {
  private readonly _http = inject(HttpClient);
  private readonly _state = signalState<State>({
    q: '',
    sort: { property: 'name', direction: 'asc' },
    pagination: { number: 1, size: 10, totalElements: 0, totalPages: 0 },
    users: null,
  });

  readonly searchQuery = this._state.q;
  readonly sort = this._state.sort;
  readonly pagination = this._state.pagination;
  readonly users = this._state.users;

  async loadUsers(options: Partial<Searchable & Pageable & Sortable> = {}): Promise<void> {
    const { q = this.searchQuery(), page = this.pagination(), sort = this.sort() } = options;
    const params = {
      q,
      page: page.number.toString(),
      size: page.size.toString(),
      orderBy: sort.property,
      sort: sort.direction,
    };

    patchState(this._state, { q, sort });

    const { content, ...pagination } = await firstValueFrom(
      this._http.get<Paginated<User>>(`${environment.api}/users`, { params }),
    );
    patchState(this._state, { users: content, pagination });
  }

  fetchUser(id: string): Promise<User> {
    return firstValueFrom(this._http.get<User>(`${environment.api}/users/${id}`));
  }

  createUser(data: CreateUser): Promise<User> {
    return firstValueFrom(this._http.post<User>(`${environment.api}/users`, data));
  }

  updateUser(id: string, data: UpdateUser): Promise<User> {
    return firstValueFrom(this._http.patch<User>(`${environment.api}/users/${id}`, data));
  }

  archiveUser(id: string): Promise<void> {
    return firstValueFrom(this._http.post<void>(`${environment.api}/users/${id}/archive`, null));
  }

  restoreUser(id: string): Promise<void> {
    return firstValueFrom(this._http.post<void>(`${environment.api}/users/${id}/restore`, null));
  }

  fetchCompanies(q: string = ''): Promise<Company[]> {
    const params = { q, size: 10, orderBy: 'createdAt', sort: 'desc' };

    return firstValueFrom(
      this._http.get<Paginated<Company>>(`${environment.api}/companies`, { params }).pipe(map(pluck('content'))),
    );
  }
}
